import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelSelect as _vModelSelect, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "absolute inset-0 overflow-y-auto bg-slate-50 dark:bg-slate-900 p-4" }
const _hoisted_2 = { class: "max-w-4xl mx-auto flex flex-col gap-6 pb-20" }
const _hoisted_3 = { class: "bg-card border rounded-lg p-4 shadow-sm" }
const _hoisted_4 = { class: "text-sm font-semibold mb-3 flex items-center text-muted-foreground" }
const _hoisted_5 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }
const _hoisted_6 = { class: "flex flex-col gap-1 text-sm" }
const _hoisted_7 = { class: "flex flex-col gap-1 text-sm" }
const _hoisted_8 = { class: "bg-primary/5 border-b border-primary/20 p-3 flex items-center justify-between" }
const _hoisted_9 = { class: "flex items-center gap-2 flex-1" }
const _hoisted_10 = ["onUpdate:modelValue"]
const _hoisted_11 = { class: "flex items-center gap-2" }
const _hoisted_12 = ["onClick"]
const _hoisted_13 = { class: "p-3 flex flex-col gap-2" }
const _hoisted_14 = {
  key: 0,
  class: "text-xs text-muted-foreground italic text-center p-4"
}
const _hoisted_15 = { class: "flex flex-col gap-1 flex-1 min-w-[200px]" }
const _hoisted_16 = ["onUpdate:modelValue"]
const _hoisted_17 = { class: "flex flex-col gap-1 w-[80px]" }
const _hoisted_18 = ["onUpdate:modelValue"]
const _hoisted_19 = { class: "flex flex-col gap-1 w-[100px]" }
const _hoisted_20 = ["onUpdate:modelValue"]
const _hoisted_21 = { class: "flex flex-col gap-1 w-[120px]" }
const _hoisted_22 = ["onUpdate:modelValue"]
const _hoisted_23 = { class: "flex flex-col gap-1 w-[80px]" }
const _hoisted_24 = ["onUpdate:modelValue"]
const _hoisted_25 = { class: "flex flex-col justify-end pb-0.5" }
const _hoisted_26 = ["onClick"]
const _hoisted_27 = { class: "text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0" }

import { ref, watch } from 'vue'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, RefreshCw, CalendarRange, GripVertical, Settings } from 'lucide-vue-next'


export default /*@__PURE__*/_defineComponent({
  __name: 'GanttEditorDialog',
  props: {
    open: { type: Boolean }
  },
  emits: ["update:open", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const showCode = ref(false)
const copied = ref(false)
const editableCode = ref('')
const isParsing = ref(false)

// Configuración global del Gantt
const config = ref({
  title: 'Cronograma del Proyecto',
  dateFormat: 'YYYY-MM-DD',
})

// Estructura de datos del Gantt
const sections = ref<any[]>([])

const initData = () => {
  config.value = {
    title: 'Cronograma del Proyecto',
    dateFormat: 'YYYY-MM-DD',
  }
  sections.value = [
    {
      id: `sec_${Date.now()}`,
      name: 'Fase 1: Planificación',
      tasks: [
        { id: 't1', name: 'Reunión inicial', status: 'done', start: '2024-01-01', duration: '2d' },
        { id: 't2', name: 'Análisis de requisitos', status: 'active', start: 'after t1', duration: '5d' }
      ]
    }
  ]
}

watch(() => props.open, (val) => {
  if (val) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (val && sections.value.length === 0) initData()
}, { immediate: true })

const addSection = () => {
  sections.value.push({
    id: `sec_${Date.now()}`,
    name: 'Nueva Sección',
    tasks: []
  })
}

const addTask = (sectionIndex: number) => {
  const taskNumber = sections.value[sectionIndex].tasks.length + 1
  sections.value[sectionIndex].tasks.push({
    id: `task_${Date.now()}`,
    name: `Tarea ${taskNumber}`,
    status: '', // active, done, crit, milestone
    start: '', // fecha o 'after id'
    duration: '1d'
  })
}

const removeSection = (index: number) => {
  sections.value.splice(index, 1)
}

const removeTask = (sectionIndex: number, taskIndex: number) => {
  sections.value[sectionIndex].tasks.splice(taskIndex, 1)
}

const generateCodeString = () => {
  let mermaid = '\n```mermaid\ngantt\n'
  mermaid += `    title ${config.value.title}\n`
  mermaid += `    dateFormat ${config.value.dateFormat}\n\n`
  
  sections.value.forEach(sec => {
    mermaid += `    section ${sec.name}\n`
    sec.tasks.forEach((t: any) => {
      let line = `    ${t.name.padEnd(20, ' ')} :`
      
      const properties = []
      if (t.status) properties.push(t.status)
      if (t.id) properties.push(t.id)
      if (t.start) properties.push(t.start)
      if (t.duration) properties.push(t.duration)
      
      line += properties.join(', ')
      mermaid += line + '\n'
    })
    mermaid += '\n'
  })

  mermaid += '```\n'
  return mermaid
}

watch([config, sections], () => {
  if (isParsing.value) return
  editableCode.value = generateCodeString()
}, { deep: true, immediate: true })

const syncToVisual = () => {
  isParsing.value = true
  try {
    const lines = editableCode.value.split('\n')
    const newSections: any[] = []
    let currentSection: any = null
    
    lines.forEach(line => {
      line = line.trim()
      if (!line || line.startsWith('```') || line.startsWith('gantt')) return

      if (line.startsWith('title ')) {
        config.value.title = line.replace('title ', '').trim()
        return
      }
      
      if (line.startsWith('dateFormat ')) {
        config.value.dateFormat = line.replace('dateFormat ', '').trim()
        return
      }

      if (line.startsWith('section ')) {
        currentSection = {
          id: `sec_${Date.now()}_${Math.random()}`,
          name: line.replace('section ', '').trim(),
          tasks: []
        }
        newSections.push(currentSection)
        return
      }

      // Tareas: Nombre de la tarea : [estado], [id], [inicio], [duración]
      if (line.includes(':') && currentSection) {
        const parts = line.split(':')
        const name = parts[0].trim()
        const propsRaw = parts.slice(1).join(':').split(',').map(s => s.trim())
        
        let status = ''
        let id = ''
        let start = ''
        let duration = ''

        // Heurística simple para inferir las propiedades posicionales o etiquetadas de Mermaid Gantt
        propsRaw.forEach(prop => {
          const p = prop.toLowerCase()
          if (['done', 'active', 'crit', 'milestone'].includes(p)) {
            status = p
          } else if (p.match(/^\d+[dwM]$/) || p.match(/^\d{4}-\d{2}-\d{2}$/) || p.startsWith('after ')) {
            // Si parece una fecha, una duración o un 'after id'
            if (p.startsWith('after ') || p.match(/^\d{4}-\d{2}-\d{2}$/)) {
              start = prop // Preservar camelCase/mayúsculas
            } else {
              duration = prop
            }
          } else {
            // Asumimos que es el ID (normalmente es el primer elemento no reservado si no es estado)
            if (!id) id = prop
          }
        })

        currentSection.tasks.push({
          id: id || `task_${Date.now()}_${Math.random()}`,
          name,
          status,
          start,
          duration
        })
      }
    })

    if (newSections.length > 0) {
      sections.value = newSections
    }
  } catch (error) {
    console.error("Error parsing gantt code:", error)
  } finally {
    setTimeout(() => { isParsing.value = false }, 100)
  }
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (e) {
    console.error('Failed to copy', e)
  }
}

const generateMermaid = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseVisualEditor, {
    open: __props.open,
    "onUpdate:open": _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('update:open', $event))),
    title: "Diagrama de Gantt",
    description: "Construye un cronograma organizando tus tareas en secciones. Define la duración (ej. 2d) o dependencias (ej. after t1).",
    showCode: showCode.value,
    "onUpdate:showCode": _cache[4] || (_cache[4] = ($event: any) => (showCode.value = $event)),
    copied: copied.value,
    onCopy: copyToClipboard,
    onSave: generateMermaid
  }, {
    icon: _withCtx(() => [
      _createVNode(_unref(CalendarRange), { class: "w-5 h-5 text-primary" })
    ]),
    toolbar: _withCtx(() => [
      _createVNode(_unref(Button), {
        size: "sm",
        onClick: addSection
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(Plus), { class: "w-4 h-4 mr-2" }),
          _cache[5] || (_cache[5] = _createTextVNode(" Añadir Sección", -1))
        ]),
        _: 1
      })
    ]),
    canvas: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("h3", _hoisted_4, [
              _createVNode(_unref(Settings), { class: "w-4 h-4 mr-2" }),
              _cache[6] || (_cache[6] = _createTextVNode(" Configuración Global", -1))
            ]),
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _cache[7] || (_cache[7] = _createElementVNode("label", null, "Título del Gráfico", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((config.value.title) = $event)),
                  class: "border rounded p-2 bg-background focus:ring-1 focus:ring-primary outline-none"
                }, null, 512), [
                  [_vModelText, config.value.title]
                ])
              ]),
              _createElementVNode("div", _hoisted_7, [
                _cache[8] || (_cache[8] = _createElementVNode("label", null, "Formato de Fecha (dateFormat)", -1)),
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((config.value.dateFormat) = $event)),
                  class: "border rounded p-2 bg-background focus:ring-1 focus:ring-primary outline-none font-mono",
                  placeholder: "YYYY-MM-DD"
                }, null, 512), [
                  [_vModelText, config.value.dateFormat]
                ])
              ])
            ])
          ]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (sec, sIdx) => {
            return (_openBlock(), _createElementBlock("div", {
              key: sec.id,
              class: "bg-card border-2 border-primary/20 rounded-lg p-0 shadow-sm overflow-hidden"
            }, [
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  _createVNode(_unref(GripVertical), { class: "w-4 h-4 text-muted-foreground cursor-grab opacity-50" }),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((sec.name) = $event),
                    class: "bg-transparent font-bold text-lg outline-none w-full border-b border-transparent focus:border-primary px-1",
                    placeholder: "Nombre de Sección"
                  }, null, 8, _hoisted_10), [
                    [_vModelText, sec.name]
                  ])
                ]),
                _createElementVNode("div", _hoisted_11, [
                  _createVNode(_unref(Button), {
                    variant: "ghost",
                    size: "sm",
                    onClick: ($event: any) => (addTask(sIdx)),
                    class: "h-8"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(Plus), { class: "w-4 h-4 mr-1" }),
                      _cache[9] || (_cache[9] = _createTextVNode(" Tarea", -1))
                    ]),
                    _: 1
                  }, 8, ["onClick"]),
                  _createElementVNode("button", {
                    onClick: ($event: any) => (removeSection(sIdx)),
                    class: "text-destructive hover:bg-destructive/10 p-1.5 rounded"
                  }, [
                    _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                  ], 8, _hoisted_12)
                ])
              ]),
              _createElementVNode("div", _hoisted_13, [
                (sec.tasks.length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, "No hay tareas en esta sección."))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sec.tasks, (task, tIdx) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: task.id,
                    class: "flex flex-wrap md:flex-nowrap items-start gap-2 bg-background border rounded p-2 group hover:border-primary/50 transition-colors"
                  }, [
                    _createElementVNode("div", _hoisted_15, [
                      _cache[10] || (_cache[10] = _createElementVNode("label", { class: "text-[10px] text-muted-foreground uppercase font-semibold" }, "Nombre de Tarea", -1)),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((task.name) = $event),
                        class: "text-sm border-b border-transparent focus:border-primary bg-transparent outline-none w-full",
                        placeholder: "Nombre"
                      }, null, 8, _hoisted_16), [
                        [_vModelText, task.name]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_17, [
                      _cache[11] || (_cache[11] = _createElementVNode("label", { class: "text-[10px] text-muted-foreground uppercase font-semibold" }, "ID", -1)),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((task.id) = $event),
                        class: "text-xs border rounded px-2 py-1 bg-muted/50 focus:bg-background outline-none font-mono",
                        placeholder: "id"
                      }, null, 8, _hoisted_18), [
                        [_vModelText, task.id]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_19, [
                      _cache[13] || (_cache[13] = _createElementVNode("label", { class: "text-[10px] text-muted-foreground uppercase font-semibold" }, "Estado", -1)),
                      _withDirectives(_createElementVNode("select", {
                        "onUpdate:modelValue": ($event: any) => ((task.status) = $event),
                        class: "text-xs border rounded px-2 py-1 bg-muted/50 focus:bg-background outline-none"
                      }, [...(_cache[12] || (_cache[12] = [
                        _createElementVNode("option", { value: "" }, "Normal", -1),
                        _createElementVNode("option", { value: "done" }, "Completado", -1),
                        _createElementVNode("option", { value: "active" }, "Activo", -1),
                        _createElementVNode("option", { value: "crit" }, "Crítico", -1),
                        _createElementVNode("option", { value: "milestone" }, "Hito", -1)
                      ]))], 8, _hoisted_20), [
                        [_vModelSelect, task.status]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_21, [
                      _cache[14] || (_cache[14] = _createElementVNode("label", { class: "text-[10px] text-muted-foreground uppercase font-semibold" }, "Inicio", -1)),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((task.start) = $event),
                        class: "text-xs border rounded px-2 py-1 bg-muted/50 focus:bg-background outline-none",
                        placeholder: "2024-01-01 o 'after t1'"
                      }, null, 8, _hoisted_22), [
                        [_vModelText, task.start]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_23, [
                      _cache[15] || (_cache[15] = _createElementVNode("label", { class: "text-[10px] text-muted-foreground uppercase font-semibold" }, "Duración", -1)),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((task.duration) = $event),
                        class: "text-xs border rounded px-2 py-1 bg-muted/50 focus:bg-background outline-none",
                        placeholder: "2d, 1w"
                      }, null, 8, _hoisted_24), [
                        [_vModelText, task.duration]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_25, [
                      _createElementVNode("button", {
                        onClick: ($event: any) => (removeTask(sIdx, tIdx)),
                        class: "text-destructive/50 hover:text-destructive p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      }, [
                        _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                      ], 8, _hoisted_26)
                    ])
                  ]))
                }), 128))
              ])
            ]))
          }), 128))
        ])
      ])
    ]),
    "code-panel": _withCtx(() => [
      _createElementVNode("div", _hoisted_27, [
        _cache[17] || (_cache[17] = _createTextVNode(" Mermaid (Sincronizado) ", -1)),
        _createVNode(_unref(Button), {
          size: "sm",
          variant: "default",
          class: "h-7 text-xs",
          onClick: syncToVisual
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(RefreshCw), { class: "w-3 h-3 mr-2" }),
            _cache[16] || (_cache[16] = _createTextVNode(" Sincronizar a Visual ", -1))
          ]),
          _: 1
        })
      ]),
      _createVNode(MermaidCodeEditor, {
        modelValue: editableCode.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((editableCode).value = $event)),
        class: "flex-1 min-h-0"
      }, null, 8, ["modelValue"])
    ]),
    "live-preview": _withCtx(() => [
      _createVNode(MermaidLivePreview, { code: editableCode.value }, null, 8, ["code"])
    ]),
    _: 1
  }, 8, ["open", "showCode", "copied"]))
}
}

})